/**
 * Geometry for the chat-dock flight surface ("chat docks to header").
 *
 * In the desktop split view the chat column is sized by CSS:
 *
 *   .chat-box.chat-box-module-fullscreen {
 *     grid-template-columns: minmax(0, 1fr) var(--chat-split-right-col);
 *   }
 *   --chat-split-right-col: clamp(360px, 33vw, 540px)   (ChatBox.css)
 *
 * The flight surface is position:fixed (portaled above the fixed header so it
 * is never clipped by the chat pane's overflow/stacking contexts), so it can't
 * be grid-placed — we replicate the clamp here instead. One computation per
 * dock/undock transition, no per-frame DOM measuring. The root rect is stable
 * across all dock phases (the chat-box root spans the whole split area), so
 * this is valid both before the column collapses and before it re-expands.
 *
 * KEEP IN SYNC with `--chat-split-right-col` in ChatBox.css.
 */

import type { DockRect } from '@/common/motion/chatDockMotion';

export type { DockRect };

export const CHAT_SPLIT_COL_MIN_PX = 360;
export const CHAT_SPLIT_COL_MAX_PX = 540;
export const CHAT_SPLIT_COL_VW_FRACTION = 0.33;

/** Resolved width of the split-view chat column: clamp(360px, 33vw, 540px). */
export function computeChatSplitColumnWidth(viewportWidth: number): number {
  return Math.min(
    Math.max(CHAT_SPLIT_COL_MIN_PX, viewportWidth * CHAT_SPLIT_COL_VW_FRACTION),
    CHAT_SPLIT_COL_MAX_PX,
  );
}

/**
 * Rect of the chat column — right-aligned inside the chat-box root rect.
 * Used as the flight surface's resting position for both directions:
 * the lift-off origin when docking, the landing target when undocking.
 */
export function computeChatColumnRect(rootRect: DockRect, viewportWidth: number): DockRect {
  const width = Math.min(computeChatSplitColumnWidth(viewportWidth), rootRect.width);
  return {
    top: rootRect.top,
    left: rootRect.left + rootRect.width - width,
    width,
    height: rootRect.height,
  };
}
